import { useTranslation } from 'react-i18next'
import { buildReportHeadline } from '../lib/reportHeadline'
import {
  deleteSighting,
  updateSightingNotes,
} from '../lib/sightingsApi'
import type { Sighting } from '../types/models'
import { useAuth } from '../auth/AuthContext'
import { EditableNotes } from './EditableNotes'
import { LinkedText } from './LinkedText'
import { ModeratorDeleteButton } from './ModeratorDeleteButton'
import { ProfileDetailWithGallery } from './ProfileDetailWithGallery'
import { ReportMessageForm } from './ReportMessageForm'
import { ReportTranslation } from './ReportTranslation'

interface Props {
  sighting: Sighting
  images: string[]
  relatedCount: number
  relatedIndex: number
  loading?: boolean
  onClose: () => void
  onStepPrev?: () => void
  onStepNext?: () => void
  onDeleted?: () => void
  onUpdated?: (sighting: Sighting) => void
}

function formatWhen(value: string, locale: string) {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleString(locale, {
    dateStyle: 'medium',
    timeStyle: 'short',
  })
}

export function SightingDetailPanel({
  sighting,
  images,
  relatedCount,
  relatedIndex,
  loading = false,
  onClose,
  onStepPrev,
  onStepNext,
  onDeleted,
  onUpdated,
}: Props) {
  const { t, i18n } = useTranslation()
  const { user } = useAuth()
  const locale = i18n.resolvedLanguage || 'en'
  const canEditNotes =
    Boolean(user) &&
    (user!.id === sighting.reported_by_user_id ||
      user!.moderator ||
      user!.admin)
  const colors = [sighting.color1, sighting.color2, sighting.color3].filter(
    Boolean,
  ) as string[]
  const title = buildReportHeadline(
    {
      kind: 'found',
      type: sighting.type,
      gender: sighting.gender,
      spayed: sighting.spayed,
      chip_status: sighting.chip_status,
      temperament: sighting.temperament,
    },
    t,
    locale,
  )

  return (
    <section className="profile-dock" aria-label={title}>
      <div className="panel-header panel-header-row">
        <h2 className="panel-title panel-title-summary">{title}</h2>
        <button
          type="button"
          className="btn btn-ghost panel-close"
          onClick={onClose}
        >
          {t('panel.closePanel')}
        </button>
      </div>

      <div className="panel-body profile-dock-body">
        {loading ? (
          <p className="empty-state">{t('status.loading')}</p>
        ) : (
          <>
            <ProfileDetailWithGallery
              imageKeys={images}
              alt={t('profile.sightingPhoto', { id: sighting.id })}
            >
              <dl className="profile-detail-grid">
                <div>
                  <dt>{t('fields.type')}</dt>
                  <dd>{t(`types.${sighting.type}`)}</dd>
                </div>
                <div>
                  <dt>{t('fields.gender')}</dt>
                  <dd>{t(`gender.${sighting.gender ?? 'unknown'}`)}</dd>
                </div>
                <div>
                  <dt>{t('fields.fixed')}</dt>
                  <dd>
                    {sighting.spayed === 1
                      ? t('fixed.yes')
                      : sighting.spayed === 0
                        ? t('fixed.no')
                        : t('fixed.unknown')}
                  </dd>
                </div>
                <div>
                  <dt>{t('fields.color')}</dt>
                  <dd>
                    {colors.map((key) => t(`colors.${key}`)).join(' / ') ||
                      t('fields.unknown')}
                  </dd>
                </div>
                <div>
                  <dt>{t('fields.temperament')}</dt>
                  <dd>
                    {t(`temperament.${sighting.temperament ?? 'not_sure'}`)}
                  </dd>
                </div>
                <div>
                  <dt>{t('fields.chip')}</dt>
                  <dd>
                    {sighting.chip_status === 'known' && sighting.chip
                      ? sighting.chip
                      : t(`chip.${sighting.chip_status ?? 'unknown'}`)}
                  </dd>
                </div>
                <div>
                  <dt>{t('fields.sightedAt')}</dt>
                  <dd>{formatWhen(sighting.sighted_at, locale)}</dd>
                </div>
                <div>
                  <dt>{t('fields.location')}</dt>
                  <dd>
                    {sighting.location_fuzzed
                      ? t('location.approximate')
                      : `${sighting.lat.toFixed(5)}, ${sighting.lng.toFixed(5)}`}
                  </dd>
                </div>
                {(sighting.willing_to_foster === 1 ||
                  sighting.willing_to_adopt === 1) && (
                  <div className="profile-detail-wide">
                    <dt>{t('profile.careIntent')}</dt>
                    <dd>
                      {[
                        sighting.willing_to_adopt === 1
                          ? t('profile.willingAdopt')
                          : null,
                        sighting.willing_to_foster === 1
                          ? t('profile.willingFoster')
                          : null,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </dd>
                  </div>
                )}
              </dl>

              {canEditNotes ? (
                <EditableNotes
                  ownerUserId={sighting.reported_by_user_id}
                  value={sighting.notes}
                  onSave={async (notes) => {
                    const updated = await updateSightingNotes(
                      sighting.id,
                      notes,
                      i18n.resolvedLanguage || i18n.language || 'en',
                    )
                    onUpdated?.(updated)
                  }}
                />
              ) : (
                <ReportTranslation
                  entryKey={`sighting-${sighting.id}`}
                  sourceLocale={sighting.source_locale}
                  fields={{ notes: sighting.notes }}
                >
                  {(text) =>
                    text.notes ? (
                      <dl className="profile-detail-grid">
                        <div className="profile-detail-wide">
                          <dt>{t('fields.notes')}</dt>
                          <dd>
                            <LinkedText text={text.notes} />
                          </dd>
                        </div>
                      </dl>
                    ) : null
                  }
                </ReportTranslation>
              )}

              <ModeratorDeleteButton
                onDelete={async () => {
                  await deleteSighting(sighting.id)
                  onDeleted?.()
                }}
              />
            </ProfileDetailWithGallery>

            <ReportMessageForm
              kind="sighting"
              reportId={sighting.id}
              ownerUserId={sighting.reported_by_user_id}
            />

            {relatedCount > 1 && (
              <div className="profile-related-nav">
                <button
                  type="button"
                  className="btn"
                  onClick={onStepPrev}
                  disabled={relatedIndex <= 0}
                >
                  {t('panel.stepPrev')}
                </button>
                <span>
                  {t('panel.related')} {relatedIndex + 1}/{relatedCount}
                </span>
                <button
                  type="button"
                  className="btn"
                  onClick={onStepNext}
                  disabled={relatedIndex >= relatedCount - 1}
                >
                  {t('panel.stepNext')}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  )
}
